import { isEqual } from 'lodash'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { withNamespaces } from 'react-i18next'
import { connect } from 'react-redux'
import { compose } from 'redux'
import { ThemeProvider } from 'styled-components'
import AlignmentRow from '../../../../../../assessment/containers/QuestionMetadata/AlignmentRow'
import { themes } from '../../../../../../theme'
import {
  getDictCurriculumsAction,
  getDictStandardsForCurriculumAction,
} from '../../../../../src/actions/dictionaries'
import {
  getCurriculumsListSelector,
  getStandardsListSelector,
  isCurriculumLoadingSelector,
  standardsSelector,
} from '../../../../../src/selectors/dictionaries'

const defaultAlignment = {
  standards: [],
  grades: [],
  domains: [],
  subject: '',
  curriculumId: '',
  curriculum: '',
}

const StandardSet = ({
  t,
  alignment,
  qId,
  onUpdate,
  curriculums,
  getCurriculums,
  getCurriculumStandards,
  curriculumStandards,
  curriculumStandardsLoading,
  isDocBased,
  showIconBrowserBtn,
  hideLabel,
  standardsRequiredFields = [],
  rubrics,
  disabled,
  isAIFlow,
  trackingEventSource,
  height,
  inputPaddingRight,
  inputPaddingLeft,
  inputMargin,
  inputBorderColor,
  isDynamicTest,
  isFetchingStandards,
  isCurriculumLoading,
  enableSelectAll,
}) => {
  const [searchProps, setSearchProps] = useState({
    id: '',
    grades: [],
    searchStr: '',
  })

  useEffect(() => {
    if (curriculums.length === 0 && !isCurriculumLoading) {
      getCurriculums()
    }
  }, [curriculums, isCurriculumLoading])

  const questionAlignment = alignment.length ? alignment[0] : defaultAlignment

  const searchCurriculumStandards = (searchObject) => {
    if (!isEqual(searchProps, searchObject)) {
      setSearchProps(searchObject)
      getCurriculumStandards(
        searchObject.id,
        searchObject.grades,
        searchObject.searchStr
      )
    }
  }

  /**
   * isInitialLoad is used to avoid updating the standard details on the first load
   * of the alignment row. Getting used in Dynamic Test only.
   */
  const handleEditAlignment = (index, standardSet, isInitialLoad? = false) => {
    const oldAlignment = alignment.length ? alignment[0] : defaultAlignment

    onUpdate(
      {
        alignment: [
          {
            ...oldAlignment,
            ...standardSet,
          },
        ],
      },
      isInitialLoad
    )
  }

  const handleDelete = (curriculumId) => () => {
    const filteredAlignment = alignment.filter(
      (item) => item.curriculumId !== curriculumId
    )

    onUpdate({
      alignment: filteredAlignment,
    })
  }

  const handleUpdateQuestionAlignment = (index, standardSet) => {
    const { domains } = standardSet

    if (!isEqual(questionAlignment.domains, domains)) {
      handleEditAlignment(index, { domains })
    }
  }

  const handleCreateGradeAndSubjects = () => {}

  return (
    <ThemeProvider theme={themes.default}>
      <AlignmentRow
        disabled={disabled}
        hideLabel={hideLabel}
        t={t}
        alignment={questionAlignment}
        alignmentIndex={0}
        qId={qId}
        curriculums={curriculums}
        curriculumStandardsELO={curriculumStandards.elo}
        curriculumStandardsTLO={curriculumStandards.tlo}
        curriculumStandardsLoading={curriculumStandardsLoading}
        handleUpdateQuestionAlignment={handleUpdateQuestionAlignment}
        onDelete={handleDelete}
        getCurriculumStandards={searchCurriculumStandards}
        editAlignment={handleEditAlignment}
        createUniqGradeAndSubjects={handleCreateGradeAndSubjects}
        isDocBased={isDocBased}
        showIconBrowserBtn={showIconBrowserBtn}
        standardsRequiredFields={standardsRequiredFields}
        standardsFromRubric={rubrics?.standardIds}
        isAIFlow={isAIFlow}
        trackingEventSource={trackingEventSource}
        height={height}
        inputPaddingRight={inputPaddingRight}
        inputPaddingLeft={inputPaddingLeft}
        inputMargin={inputMargin}
        inputBorderColor={inputBorderColor}
        isDynamicTest={isDynamicTest}
        isFetchingStandards={isFetchingStandards}
        enableSelectAll={enableSelectAll}
      />
    </ThemeProvider>
  )
}

StandardSet.propTypes = {
  t: PropTypes.func.isRequired,
  alignment: PropTypes.array,
  curriculums: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      curriculum: PropTypes.string.isRequired,
      grades: PropTypes.array.isRequired,
      subject: PropTypes.string.isRequired,
    })
  ).isRequired,
  curriculumStandards: PropTypes.object.isRequired,
  curriculumStandardsLoading: PropTypes.bool.isRequired,
  onUpdate: PropTypes.func.isRequired,
  getCurriculums: PropTypes.func.isRequired,
  getCurriculumStandards: PropTypes.func.isRequired,
  standardsRequiredFields: PropTypes.array,
}

StandardSet.defaultProps = {
  alignment: [],
  standardsRequiredFields: [],
}

const mapStateToProps = (state) => ({
  curriculums: getCurriculumsListSelector(state),
  isCurriculumLoading: isCurriculumLoadingSelector(state),
  curriculumStandardsLoading: standardsSelector(state).loading,
  curriculumStandards: getStandardsListSelector(state),
})

const mapDispatchToProps = {
  getCurriculums: getDictCurriculumsAction,
  getCurriculumStandards: getDictStandardsForCurriculumAction,
}

const enhance = compose(
  withNamespaces('assessment'),
  connect(mapStateToProps, mapDispatchToProps)
)

export default enhance(StandardSet)
